import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import moment from 'moment'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TuneIcon from '@mui/icons-material/Tune'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { ReactComponent as SearchIcon } from 'assets/images/search.svg'
import { getTaskBuildings, getTaskAssignees, getTaskApprovers } from 'services/taskService'
import {
  selectTaskFilters,
  selectTasks,
  selectProjects,
  selectFormTemplates,
  selectTeams,
  selectActiveFilterCount
} from '../ducks/selectors'
import {
  setTaskFiltersRoutine,
  clearTaskFiltersRoutine,
  getFormTemplatesRoutine
} from '../ducks/actions'
import { TASK_STATUS_OPTIONS } from 'features/taskManagement/taskStatusConstants'
import { fetchTaskDomains } from 'services/ProgrammeService'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import TaskTypeSegment from './TaskTypeSegment'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  ...TASK_STATUS_OPTIONS,
  { value: 'overdue', label: 'Overdue' },
]

const priorityOptions = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: PRIORITY_META.critical.label }
]

const sortOptions = [
  { value: '-freshness', label: 'Freshest first' },
  { value: '-updated_at', label: 'Latest update' },
  { value: '-completed_at', label: 'Completion time' },
  { value: 'deadline', label: 'Deadline (soonest)' },
  { value: '-created_at', label: 'Recently created' }
]

const TaskFilterBar = ({ searchValue = '', onSearchChange, onExport }) => {
  const dispatch = useDispatch()
  const filters = useSelector(selectTaskFilters)
  const tasks = useSelector(selectTasks)
  const projects = useSelector(selectProjects)
  const formTemplates = useSelector(selectFormTemplates)
  const teams = useSelector(selectTeams)
  const activeFilterCount = useSelector(selectActiveFilterCount)

  const [openDropdown, setOpenDropdown] = useState(null)
  const [domains, setDomains] = useState([])
  const [showAdvanced, setShowAdvanced] = useState(false)
  const dropdownRef = useRef(null)
  const didInitAdvanced = useRef(false)

  useEffect(() => {
    if (!formTemplates.length) {
      dispatch(getFormTemplatesRoutine())
    }
  }, [dispatch, formTemplates.length])

  useEffect(() => {
    let active = true
    fetchTaskDomains()
      .then(res => {
        if (!active) return
        const list = res?.data?.data ?? res?.data ?? []
        setDomains(list.filter(d => d.isActive !== false))
      })
      .catch(() => {
        if (active) setDomains([])
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const [serverBuildings, setServerBuildings] = useState(null)
  const [serverAssignees, setServerAssignees] = useState(null)
  const [serverApprovers, setServerApprovers] = useState(null)
  useEffect(() => {
    let cancelled = false
    getTaskBuildings()
      .then(res => { if (!cancelled) setServerBuildings(res.data?.data || []) })
      .catch(() => {})
    getTaskAssignees()
      .then(res => { if (!cancelled) setServerAssignees(res.data?.data || []) })
      .catch(() => {})
    getTaskApprovers()
      .then(res => { if (!cancelled) setServerApprovers(res.data?.data || []) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const buildings = useMemo(() => {
    if (serverBuildings && serverBuildings.length) {
      return serverBuildings.map(b => ({ value: b.id, label: b.name }))
    }
    const map = {}
    tasks.forEach(t => {
      if (t.building?.id) {
        map[t.building.id] = t.building.name
      }
    })
    return Object.entries(map)
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [serverBuildings, tasks])

  const currentUser = useSelector(getCurrentUser)
  const assignees = useMemo(() => {
    let list
    if (serverAssignees && serverAssignees.length) {
      list = serverAssignees.map(a => ({
        value: a.id,
        label: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email
      }))
    } else {
      const map = {}
      tasks.forEach(t => {
        t.assignees?.forEach(a => {
          if (a.id) {
            map[a.id] =
              `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email
          }
        })
      })
      list = Object.entries(map)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
    if (currentUser?.id) {
      return [
        { value: currentUser.id, label: 'Assigned to me' },
        ...list.filter(a => a.value !== currentUser.id)
      ]
    }
    return list
  }, [serverAssignees, tasks, currentUser])

  const approvers = useMemo(() => {
    let list
    if (serverApprovers && serverApprovers.length) {
      list = serverApprovers.map(a => ({
        value: a.id,
        label: `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email
      }))
    } else {
      const map = {}
      tasks.forEach(t => {
        t.approvers?.forEach(a => {
          if (a.id) {
            map[a.id] =
              `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email
          }
        })
      })
      list = Object.entries(map)
        .map(([id, name]) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
    if (currentUser?.id) {
      return [
        { value: currentUser.id, label: 'For my sign-off' },
        ...list.filter(a => a.value !== currentUser.id)
      ]
    }
    return list
  }, [serverApprovers, tasks, currentUser])

  const setFilter = (key, value) => {
    dispatch(setTaskFiltersRoutine({ [key]: value || null }))
    setOpenDropdown(null)
  }

  const handleClear = () => {
    dispatch(clearTaskFiltersRoutine())
  }

  const advancedActiveCount = [
    filters.priority,
    filters.approverId,
    filters.projectId,
    filters.formTemplateId,
    filters.teamId,
    filters.deadlineFrom || filters.deadlineTo,
    filters.createdFrom || filters.createdTo
  ].filter(Boolean).length

  useEffect(() => {
    if (!didInitAdvanced.current && advancedActiveCount > 0) {
      setShowAdvanced(true)
      didInitAdvanced.current = true
    }
  }, [advancedActiveCount])

  const renderDropdown = (key, label, options, currentValue, filterKey) => {
    const optSelected = opt => opt.selectedWhen
      ? filters[opt.selectedWhen.key] === opt.selectedWhen.value
      : opt.value === currentValue
    const specialActive = options.find(o => o.selectedWhen && optSelected(o))
    const isActive = specialActive
      ? true
      : filterKey === 'status'
        ? currentValue && currentValue !== 'all'
        : !!currentValue
    const activeOption = specialActive || options.find(o => o.value === currentValue)
    const activeLabel = activeOption?.label

    return (
      <FilterPill
        $active={isActive}
        onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
        data-tour-id={key === 'status' ? 'tasks.filter-status' : undefined}
      >
        {isActive && activeOption?.colour && (
          <Swatch style={{ background: activeOption.colour }} />
        )}
        <span>{isActive ? activeLabel : label}</span>
        {isActive && (
          <PillClear
            onClick={e => {
              e.stopPropagation()
              if (specialActive?.setFilters) {
                const cleared = {}
                Object.keys(specialActive.setFilters).forEach(k => { cleared[k] = k === 'status' ? 'all' : null })
                dispatch(setTaskFiltersRoutine(cleared))
                setOpenDropdown(null)
              } else {
                setFilter(filterKey, filterKey === 'status' ? 'all' : null)
              }
            }}
          >
            <CloseIcon />
          </PillClear>
        )}
        {openDropdown === key && (
          <Dropdown data-tour-id={key === 'status' ? 'tasks.filter-status-menu' : undefined}>
            {options.map(opt => (
              <DropdownItem
                key={opt.value}
                $selected={optSelected(opt)}
                onClick={e => {
                  e.stopPropagation()
                  if (opt.setFilters) {
                    dispatch(setTaskFiltersRoutine(opt.setFilters))
                    setOpenDropdown(null)
                  } else if (specialActive?.setFilters) {
                    const patch = { [filterKey]: opt.value || null }
                    Object.keys(specialActive.setFilters).forEach(k => { if (k !== filterKey) patch[k] = k === 'status' ? 'all' : null })
                    dispatch(setTaskFiltersRoutine(patch))
                    setOpenDropdown(null)
                  } else {
                    setFilter(filterKey, opt.value)
                  }
                }}
              >
                {opt.colour && (
                  <Swatch style={{ background: opt.colour }} />
                )}
                {opt.label}
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </FilterPill>
    )
  }

  const dateRangeLabel = (from, to) => {
    const f = from ? moment(from).format('DD MMM') : null
    const t = to ? moment(to).format('DD MMM') : null
    if (f && t) return `${f} – ${t}`
    if (f) return `from ${f}`
    if (t) return `until ${t}`
    return null
  }

  const renderDateFilter = (key, label, fromKey, toKey, Icon) => {
    const from = filters[fromKey]
    const to = filters[toKey]
    const isActive = !!(from || to)
    const clearDates = () => dispatch(setTaskFiltersRoutine({ [fromKey]: null, [toKey]: null }))

    return (
      <FilterPill
        $active={isActive}
        onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
      >
        <Icon style={{ fontSize: 15 }} />
        <span>{isActive ? `${label}: ${dateRangeLabel(from, to)}` : label}</span>
        {isActive && (
          <PillClear onClick={e => { e.stopPropagation(); clearDates() }}>
            <CloseIcon />
          </PillClear>
        )}
        {openDropdown === key && (
          <DatePop onClick={e => e.stopPropagation()}>
            <DateRow>
              <DateFieldLabel>From</DateFieldLabel>
              <PopDateInput
                type='date'
                value={from || ''}
                onChange={e => setFilter(fromKey, e.target.value)}
              />
            </DateRow>
            <DateRow>
              <DateFieldLabel>To</DateFieldLabel>
              <PopDateInput
                type='date'
                value={to || ''}
                onChange={e => setFilter(toKey, e.target.value)}
              />
            </DateRow>
            {isActive && (
              <ClearDatesBtn onClick={clearDates}>Clear dates</ClearDatesBtn>
            )}
          </DatePop>
        )}
      </FilterPill>
    )
  }

  const projectOptions = [
    { value: '', label: 'All Projects' },
    ...[...projects]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(p => ({ value: p.id, label: p.name }))
  ]

  const buildingOptions = [{ value: '', label: 'All Building Passports' }, ...buildings]

  const assigneeOptions = [
    { value: '', label: 'All Assignees' },
    { value: 'unassigned', label: 'Unassigned', setFilters: { status: 'unassigned', assignedTo: null }, selectedWhen: { key: 'status', value: 'unassigned' } },
    ...assignees
  ]

  const approverOptions = [{ value: '', label: 'All Approvers' }, ...approvers]

  const templateOptions = [
    { value: '', label: 'All Templates' },
    ...formTemplates
      .filter(t => !t.hiddenFromLibrary)
      .map(t => ({ value: t.id, label: t.name }))
  ]

  const teamOptions = [
    { value: '', label: 'All Teams' },
    ...teams.map(t => ({ value: t.id, label: t.name }))
  ]

  const domainOptions = [
    { value: '', label: 'All domains' },
    ...domains.map(d => ({ value: d.id, label: d.name, colour: d.colour }))
  ]

  return (
    <FilterBarWrapper ref={dropdownRef}>
      <PrimaryRow>
        <FilterIcon>
          <FilterListIcon />
        </FilterIcon>

        <SegWrap><TaskTypeSegment /></SegWrap>

        {renderDropdown('sort', 'Sort', sortOptions, filters.sort, 'sort')}
        {renderDropdown('status', 'Status', statusOptions, filters.status, 'status')}
        {domainOptions.length > 1 &&
          renderDropdown('domain', 'Domain', domainOptions, filters.taskDomainId, 'taskDomainId')}
        {renderDropdown('assignee', 'Assignee', assigneeOptions, filters.assignedTo, 'assignedTo')}
        {renderDropdown('building', 'Building Passport', buildingOptions, filters.buildingId, 'buildingId')}

        <MoreFiltersBtn
          type='button'
          $active={advancedActiveCount > 0}
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced(v => !v)}
        >
          <TuneIcon style={{ fontSize: 16 }} />
          <span>More filters</span>
          {advancedActiveCount > 0 && <MoreCount>{advancedActiveCount}</MoreCount>}
          <Chevron $open={showAdvanced}>
            <ExpandMoreIcon style={{ fontSize: 16 }} />
          </Chevron>
        </MoreFiltersBtn>

        {activeFilterCount > 0 && (
          <ClearAllButton onClick={handleClear}>
            Clear all ({activeFilterCount})
          </ClearAllButton>
        )}

        <RightGroup>
          <SearchWrapper>
            <SearchIcon />
            <SearchInput
              type='text'
              placeholder='Search tasks...'
              value={searchValue || ''}
              onChange={e => onSearchChange && onSearchChange(e.target.value)}
              data-tour-id='tasks.search'
            />
          </SearchWrapper>
          {onExport && (
            <ExportBtn type='button' title='Export the current task list as CSV' onClick={onExport}>
              <FileDownloadOutlinedIcon />
              <span>Export</span>
            </ExportBtn>
          )}
        </RightGroup>
      </PrimaryRow>

      {showAdvanced && (
        <AdvancedRow>
          {renderDropdown('priority', 'Priority', priorityOptions, filters.priority, 'priority')}
          {renderDropdown('approver', 'Approver', approverOptions, filters.approverId, 'approverId')}
          {renderDropdown('project', 'Project', projectOptions, filters.projectId, 'projectId')}
          {renderDropdown('template', 'Form Template', templateOptions, filters.formTemplateId, 'formTemplateId')}
          {renderDropdown('team', 'Team', teamOptions, filters.teamId, 'teamId')}
          {renderDateFilter('deadline', 'Deadline', 'deadlineFrom', 'deadlineTo', CalendarTodayIcon)}
          {renderDateFilter('created', 'Created', 'createdFrom', 'createdTo', AccessTimeIcon)}
        </AdvancedRow>
      )}
    </FilterBarWrapper>
  )
}

export default TaskFilterBar

const FilterBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 24px;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  background: white;

  @media (max-width: 768px) {
    padding: 10px 16px;
    gap: 8px;
  }
`

const PrimaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 6px;
  }
`

const AdvancedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px dashed ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};

  @media (max-width: 768px) {
    gap: 6px;
  }
`

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 768px) {
    flex-basis: 100%;
    width: 100%;
  }
`

const MoreFiltersBtn = styled.button`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.haiti || '#0C1229' : theme.colors.lightGrey || '#e5e7eb')};
  background: ${({ $active, theme }) => ($active ? theme.colors.haiti || '#0C1229' : 'white')};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.colors.haiti || '#0C1229')};

  &:hover {
    border-color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  }
`

const MoreCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.secondary || '#33becc'};
  color: #fff;
`

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  transition: transform 0.15s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 36px;
  background: ${({ theme }) => theme.colors.lightGrey || '#f3f4f6'};
  border-radius: 8px;
  flex: 1 1 120px;
  min-width: 120px;
  max-width: 320px;
  border: 1px solid transparent;
  transition: border-color 0.2s, background 0.2s;

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.5;
    flex-shrink: 0;
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.secondary || '#33becc'};
    background: white;
  }

  @media (max-width: 768px) {
    max-width: none;
  }
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }
`

const ExportBtn = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  svg { font-size: 16px; }
  &:hover { background: ${({ theme }) => theme.colors.backgroundColor || '#f3f4f6'}; }
`

const SegWrap = styled.div`
  display: inline-flex;
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex: 1 1 calc(100% - 48px);
    min-width: 0;
  }
`

const FilterIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  svg {
    font-size: 18px;
  }
`

const FilterPill = styled.div`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? theme.colors.haiti || '#0C1229'
        : theme.colors.lightGrey || '#e5e7eb'};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.haiti || '#0C1229' : 'white'};
  color: ${({ $active, theme }) =>
    $active ? 'white' : theme.colors.haiti || '#0C1229'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  }
`

const Swatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`

const PillClear = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  svg {
    font-size: 14px;
  }
  &:hover {
    opacity: 0.7;
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 180px;
  max-height: 240px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
  padding: 4px;
`

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  border-radius: 6px;
  cursor: pointer;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  background: ${({ $selected }) => ($selected ? '#f3f4f6' : 'transparent')};

  &:hover {
    background: #f3f4f6;
  }
`

const DatePop = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 210px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
`

const DateRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#4b5563'};
  cursor: pointer;
`

const DateFieldLabel = styled.span`
  min-width: 36px;
`

const PopDateInput = styled.input`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 7px;
  font-size: 12px;
  padding: 6px 8px;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  background: white;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.haiti || '#0C1229'}; }
  &::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
`

const ClearDatesBtn = styled.button`
  margin-top: 2px;
  padding: 7px 10px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey || '#e5e7eb'};
  border-radius: 8px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.haiti || '#0C1229'}; }
`

const ClearAllButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  padding: 6px 12px;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: #dc2626;
  background: #fef2f2;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
  }
`
